import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { Button, Checkbox, Field, Select } from '@bb/ui';
import { type AdminApi, type FunnelReport, type FunnelRequest, type FunnelStep, type FunnelStepResult, type ReportSource, type ReportStart } from '../../../api/client.ts';
import { NumberInput } from '../../campaigns/trigger/NumberInput.tsx';
import { CustomEventFields } from '../../campaigns/trigger/TriggerEditor.tsx';
import { PeriodPicker } from '../PeriodPicker.tsx';
import { periodProblem, type Period } from '../period.ts';
import { factStepsFor, formatDuration, FUNNEL_DEFAULT_WINDOW_DAYS, FUNNEL_MAX_STEPS, FUNNEL_MAX_WINDOW_DAYS, funnelIssues, pct, reportPeriodProblem, START_LABELS, startsFor } from './reportBuilder.ts';
import { ReportError, groupLabel } from './ReportError.tsx';

export interface ReportPanelProps {
  environmentId: string;
  source: ReportSource;
  /** Channels of a campaign: only their starts and steps are offered; a Canvas offers all. */
  channels?: readonly string[];
  period: Period;
  onPeriodChange: (period: Period) => void;
}

const newEventStep = (): FunnelStep => ({ kind: 'event', name: '', propertyFilters: [] });

/** One funnel step: a message interaction or a custom event with property filters (the trigger editor's fields). */
function StepEditor({ environmentId, index, step, channels, issues, onChange, onRemove }: { environmentId: string; index: number; step: FunnelStep; channels?: readonly string[] | undefined; issues: ReturnType<typeof funnelIssues>; onChange: (next: FunnelStep) => void; onRemove: () => void }) {
  const groups = factStepsFor(channels);
  const firstFact = groups[0]?.items[0]?.type ?? 'delivered';
  const label = `Step ${index + 1}`;
  return (
    <fieldset className="audience-group" aria-label={label}>
      <legend>{label}</legend>
      <Field label="The user">
        <Select
          options={[
            { value: 'fact', label: 'Interacts with the message' },
            { value: 'event', label: 'Performs a custom event' },
          ]}
          value={step.kind}
          onChange={(e) => onChange(e.target.value === 'fact' ? { kind: 'fact', type: firstFact } : newEventStep())}
        />
      </Field>
      {step.kind === 'fact' ? (
        <Field label="Interaction">
          <Select options={groups.flatMap((g) => g.items.map((item) => ({ value: item.type, label: `${g.group} · ${item.label}` })))} value={step.type} onChange={(e) => onChange({ kind: 'fact', type: e.target.value as Extract<FunnelStep, { kind: 'fact' }>['type'] })} />
        </Field>
      ) : (
        <CustomEventFields
          environmentId={environmentId}
          readOnly={false}
          issues={issues}
          path={['steps', index]}
          spec={{ kind: 'custom_event', event: step.name, propertyFilters: step.propertyFilters }}
          onChange={(next) => {
            if (next.kind === 'custom_event') onChange({ kind: 'event', name: next.event, propertyFilters: next.propertyFilters });
          }}
        />
      )}
      <Button size="sm" variant="ghost" aria-label={`Remove step ${index + 1}`} onClick={onRemove}>
        Remove
      </Button>
    </fieldset>
  );
}

/** Funnel bars: users per step with the share of the start, of the previous step and the median time from it. */
export function FunnelChart({ steps, label }: { steps: FunnelStepResult[]; label: string }) {
  return (
    <table className="bar-table funnel-table" aria-label={label}>
      <thead>
        <tr>
          <th scope="col">Step</th>
          <th scope="col">Users</th>
          <th scope="col" className="bar-table__bar">
            <span className="bb-visually-hidden">Share of the start</span>
          </th>
          <th scope="col">From start</th>
          <th scope="col">From previous step</th>
          <th scope="col">Median time from previous</th>
        </tr>
      </thead>
      <tbody>
        {steps.map((s) => (
          <tr key={s.index}>
            <th scope="row">
              {s.index === 0 ? 'Start: ' : `${s.index}. `}
              {s.label.en}
            </th>
            <td className="bar-table__value">{s.count.toLocaleString('en-US')}</td>
            <td className="bar-table__bar" aria-hidden="true">
              <span className="bar-table__fill" style={{ width: `${Math.round((s.rateFromStart ?? 0) * 100)}%` }} />
            </td>
            <td className="bar-table__value">{pct(s.rateFromStart)}</td>
            <td className="bar-table__value">{s.index === 0 ? '—' : pct(s.rateFromPrevious)}</td>
            <td className="bar-table__value">{formatDuration(s.medianSecondsFromPrevious)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Each variant's and control's funnel side by side: users and share of their own start per step. */
function GroupsTable({ report }: { report: FunnelReport }) {
  const groups = report.groups ?? [];
  if (groups.length === 0) return <p className="hint">No variant or control users started in this period.</p>;
  return (
    <div className="push-table-scroll">
      <table className="table push-table" aria-label="Funnel by variant">
        <thead>
          <tr>
            <th scope="col">Step</th>
            {groups.map((g) => (
              <th key={g.variantApiId} scope="col">
                {groupLabel(g)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {report.steps.map((s, i) => (
            <tr key={s.index}>
              <th scope="row">{s.index === 0 ? `Start: ${s.label.en}` : `${s.index}. ${s.label.en}`}</th>
              {groups.map((g) => (
                <td key={g.variantApiId}>
                  <span className="push-table__count">{(g.steps[i]?.count ?? 0).toLocaleString('en-US')}</span>
                  <span className="push-table__rate">{pct(g.steps[i]?.rateFromStart ?? null)}</span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Funnel report of a campaign or Canvas (BB-135, AN-003): from the first receipt (or Canvas entry) in the period through
 * up to six steps — message interactions or custom events with property filters — each within the window from the
 * start, in order. Users per step, conversion from the start and the previous step, median time; optionally per
 * variant with control. The report runs on demand (it is a POST) and the service caches it for a few minutes.
 */
export function FunnelReportPanel({ environmentId, source, channels, period, onPeriodChange }: ReportPanelProps) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const starts = startsFor(source, channels);
  const [start, setStart] = useState<ReportStart>(starts[0]!);
  const [steps, setSteps] = useState<FunnelStep[]>(() => [{ kind: 'fact', type: factStepsFor(channels)[0]?.items[0]?.type ?? 'delivered' }]);
  const [windowDays, setWindowDays] = useState<number | undefined>(FUNNEL_DEFAULT_WINDOW_DAYS);
  const [byVariant, setByVariant] = useState(false);
  const [tried, setTried] = useState(false);
  const [submitted, setSubmitted] = useState<FunnelRequest | null>(null);
  const report = useQuery({ queryKey: ['reports', environmentId, 'funnel', submitted], queryFn: () => api.funnelReport(environmentId, submitted!), enabled: submitted !== null, retry: false });

  const canvasVariants = 'canvasId' in source && start !== 'canvas_entered';
  const issues = funnelIssues(steps);
  const periodIssue = reportPeriodProblem(period);
  const windowIssue = windowDays === undefined || !Number.isInteger(windowDays) || windowDays < 1 || windowDays > FUNNEL_MAX_WINDOW_DAYS ? `Enter whole days from 1 to ${FUNNEL_MAX_WINDOW_DAYS}.` : null;
  const ready = issues.length === 0 && !periodIssue && !windowIssue && steps.length > 0;
  const run = () => {
    setTried(true);
    if (!ready) return;
    const next: FunnelRequest = { source, start, steps, windowDays: windowDays!, from: period.from, to: period.to, ...(byVariant && !canvasVariants ? { groupBy: 'variant' as const } : {}) };
    // The same settings again run the report again (e.g. after a timeout); new settings are a new report.
    if (JSON.stringify(next) === JSON.stringify(submitted)) void report.refetch();
    else setSubmitted(next);
  };
  const data = report.data;
  return (
    <section className="panel report-builder" aria-label="Funnel report">
      <h2>Funnel</h2>
      <PeriodPicker value={period} onChange={onPeriodChange} />
      {periodIssue && periodProblem(period) === null && (
        <p role="alert" className="field-error">
          {periodIssue}
        </p>
      )}
      <div className="stack-form">
        <Field label="Start">
          <Select options={starts.map((s) => ({ value: s, label: START_LABELS[s] }))} value={start} onChange={(e) => setStart(e.target.value as ReportStart)} />
        </Field>
        <p className="hint">A user starts once, at the first time in the period; every next step must happen after the previous one.</p>
        <ol className="plain-list audience-builder" aria-label="Funnel steps">
          {steps.map((step, i) => (
            <li key={i}>
              <StepEditor environmentId={environmentId} index={i} step={step} channels={channels} issues={tried ? issues : []} onChange={(next) => setSteps(steps.map((s, k) => (k === i ? next : s)))} onRemove={() => setSteps(steps.filter((_, k) => k !== i))} />
            </li>
          ))}
        </ol>
        {steps.length === 0 && tried && (
          <p role="alert" className="field-error">
            Add at least one step.
          </p>
        )}
        <Button size="sm" disabled={steps.length >= FUNNEL_MAX_STEPS} onClick={() => setSteps([...steps, newEventStep()])}>
          Add a step
        </Button>
        {steps.length >= FUNNEL_MAX_STEPS && <p className="hint">A funnel has at most {FUNNEL_MAX_STEPS} steps.</p>}
        <Field label="Window, days" hint="Each step must happen within this many days of the start." error={windowIssue ?? undefined}>
          <NumberInput min={1} max={FUNNEL_MAX_WINDOW_DAYS} step={1} value={windowDays} onValueChange={setWindowDays} />
        </Field>
        <Checkbox label="Compare variants and control" checked={byVariant && !canvasVariants} disabled={canvasVariants} onCheckedChange={setByVariant} />
        {canvasVariants && <p className="hint">Canvas variants are known from the entry: choose the start «Entered the Canvas» to compare them.</p>}
        <div>
          <Button onClick={run} loading={report.isFetching}>
            Run report
          </Button>
        </div>
      </div>
      {submitted && (
        <div className="report-result" aria-live="polite">
          {report.isError ? (
            <ReportError error={report.error} onRetry={() => void report.refetch()} />
          ) : !data ? (
            <p aria-busy="true">Running the funnel…</p>
          ) : (
            <>
              <p className="hint">
                {data.from} – {data.to}, {data.timezone}; window {data.windowDays} {data.windowDays === 1 ? 'day' : 'days'}. Computed {new Date(data.generatedAt).toLocaleString('en-GB')}; results are kept for a few minutes.
              </p>
              {data.steps[0]?.count === 0 ? <p className="hint">Nobody started in this period.</p> : <FunnelChart steps={data.steps} label="Funnel" />}
              {data.groups && <GroupsTable report={data} />}
              <details>
                <summary>How the funnel is counted</summary>
                <p>{data.definition.en}</p>
              </details>
            </>
          )}
        </div>
      )}
    </section>
  );
}

